// Sugalarm — insulin-on-board math.
//
// Cycle 9 placeholder: simple linear decay over 4 hours from each insulin
// treatment. Real PK curves come later. The dual-ring component reads a
// list of {units, takenAt, label, color} entries; this module produces
// that list from the existing /api/treatments JSON.

import { foodIconKey, matchesFoodKeyword, type FoodIconKey } from "./foodEmoji";
import type { Treatment } from "./types";

export const IOB_DURATION_MS = 4 * 60 * 60 * 1000;

// Cycle 74b: meal / correction classification + display derivation.
// A bolus and a carbs row within this window are treated as one meal.
export const MEAL_PAIR_WINDOW_MS = 30 * 60 * 1000;
// Rows older than this never contribute a meal/dose line — same horizon
// as the IOB ring window so lines and rings age out together.
export const MEAL_CONTEXT_MAX_AGE_MS = IOB_DURATION_MS;

// Cycle 121: how far back the basal line looks for the most recent basal
// dose. A 24h Lantus given last night is still the active basal this
// morning even after it's lapsed (it stays on the card "due" so the
// "take your Lantus" nudge can't disappear). 48h comfortably covers a
// missed dose without resurrecting an ancient one.
export const BASAL_LOOKBACK_MS = 48 * 60 * 60 * 1000;
// Cycle 135: the basal lookback expressed in whole hours, so the Now-screen
// treatments fetch can floor its window at this span (`max(chartHours,
// BASAL_LOOKBACK_HOURS)`) and `basalForContext` always has ≥48h of rows to
// look back over regardless of the selected chart window. Derived from
// BASAL_LOOKBACK_MS (the single source of truth) — never hardcode 48.
export const BASAL_LOOKBACK_HOURS = BASAL_LOOKBACK_MS / 3_600_000;
// Fallback per-dose duration when a basal row has no `insulin_duration_min`
// (24h Lantus). The server already defaults it to 1440 on logging, so this
// only covers a row that somehow arrived with a null duration. Never
// hardcode 24h elsewhere — the basal line reads its duration off the row.
export const DEFAULT_BASAL_DURATION_MIN = 1440;

// Single source of truth for the two dose colors — the ring arc, the
// line dot, the modal context dot, and the ring-matched countdown timer
// all read from here. Cycle 104d: the first ring (meal / first dose) is
// hunter green and the second ring (correction) is yellow, via two
// theme-invariant tokens in `theme/tokens.css` (fixed across all six
// themes); hex fallbacks keep them resolving anywhere the token is absent.
//
// Cycle 118b: the token NAMES live here too, so the field→token mapping
// (iob_meal_color → first/outer ring, iob_correction_color →
// second/inner ring) is defined in exactly one place (`iobRingColorVars`
// below), consumed by BOTH the app-root applier and the Settings preview
// wrapper — neither hardcodes the pair, so they can never invert.
export const IOB_RING_FIRST_TOKEN = "--iob-ring-first";
export const IOB_RING_SECOND_TOKEN = "--iob-ring-second";
// Cycle 147: the third (outermost, long-acting/basal) ring.
export const IOB_RING_THIRD_TOKEN = "--iob-ring-third";
const IOB_RING_FIRST_DEFAULT = "#355e3b";
const IOB_RING_SECOND_DEFAULT = "#ffd60a";
const IOB_RING_THIRD_DEFAULT = "#8b7cf6";
/** Every IOB ring token, in ring order (outer first). The applier clears
 *  any token not present in an `iobRingColorVars` bag. */
export const IOB_RING_TOKENS = [
  IOB_RING_THIRD_TOKEN,
  IOB_RING_FIRST_TOKEN,
  IOB_RING_SECOND_TOKEN,
] as const;

export const MEAL_BOLUS_COLOR = `var(${IOB_RING_FIRST_TOKEN}, ${IOB_RING_FIRST_DEFAULT})`;
export const CORRECTION_BOLUS_COLOR = `var(${IOB_RING_SECOND_TOKEN}, ${IOB_RING_SECOND_DEFAULT})`;
/** Cycle 147: the basal (long-acting) ring + Long-acting line color. */
export const BASAL_RING_COLOR = `var(${IOB_RING_THIRD_TOKEN}, ${IOB_RING_THIRD_DEFAULT})`;

/** Cycle 148: a basal with less than this many hours left (but not yet
 *  due) renders in the warm "due soon" tint below. */
export const BASAL_DUE_SOON_HOURS = 1;

/** Cycle 148 — the SINGLE basal ring/center-countdown color resolver:
 *  normal → the household-recolorable --iob-ring-third; **due soon**
 *  (0 < hoursLeft < BASAL_DUE_SOON_HOURS) → a warm color-mix blend
 *  toward the salient --zone-warn (derived, so it tracks a recolored
 *  ring); **due** → the full --zone-warn (the cycle-147 attention
 *  ring). Consumed by BolusRings for the arc AND its matching center
 *  countdown — never resolve this anywhere else. */
export function basalRingColor(basal: Pick<BasalContext, "due" | "hoursLeft">): string {
  if (basal.due) return "var(--zone-warn)";
  if (basal.hoursLeft < BASAL_DUE_SOON_HOURS) {
    return `color-mix(in srgb, ${BASAL_RING_COLOR}, var(--zone-warn) 55%)`;
  }
  return BASAL_RING_COLOR;
}

/** Cycle 343 — the SINGLE contrast-clamp for any ring/dose color used as
 *  TEXT (never for a ring stroke or a decorative dot — those stay the raw
 *  color). Ring colors are household-customizable (cycle 118, any hex) and
 *  must read on both a dark card (kiosk/web/mobile-Dark) and a light or
 *  photo-backed card (mobile-Light `MobileBackground`, where the label may
 *  additionally sit inside a translucent tint pill over a translucent
 *  glass card over an arbitrary user photo — a compositing stack too
 *  variable to target a razor-thin pass). Mixing 30% of the raw color into
 *  70% `--ink` pulls a LIGHT color (e.g. the stock correction yellow
 *  `#ffd60a`, measured ~1.2:1 raw / ~2.2:1 at the old 70/30 ratio against a
 *  light card — both WCAG AA fails) down to a measured ~5.9–6.8:1 across a
 *  pale-card-to-pure-white range — solid margin above the 4.5:1 floor,
 *  not a bare pass. On dark surfaces `--ink` is itself light, so the same
 *  mix pushes a light color even lighter — high contrast in both
 *  directions. A dark color (e.g. the stock meal hunter-green) was already
 *  legible and stays legible, just slightly more so. Previously duplicated
 *  ad hoc in `MealContextLines` (as `labelInk`, at the under-margined
 *  70/30 ratio) and left entirely unapplied in `BolusRings`' ring-center
 *  countdown/basal-readout text, which rendered the raw ring color
 *  directly (measured ~1.2–2.9:1, all AA fails). Both now import this one
 *  helper. */
export function ringLabelInk(color: string): string {
  return `color-mix(in srgb, ${color} 30%, var(--ink))`;
}

/** Cycle 118b — the SINGLE field→token mapping for the IOB ring-color
 *  setting: `iob_meal_color` drives the first/outer ring token,
 *  `iob_correction_color` the second/inner ring token. Returns a custom-
 *  property bag (token → color) usable directly as a React inline style
 *  (the Settings preview wrapper) and as the source the app-root applier
 *  diffs against the document root. A null color is omitted; callers that
 *  must CLEAR an inline override (the applier) remove tokens absent from
 *  the bag — see `IOB_RING_TOKENS`. */
export function iobRingColorVars(colors: {
  meal: string | null;
  correction: string | null;
  /** Cycle 147: `iob_basal_color` → the third (basal) ring token. */
  basal?: string | null;
}): Record<string, string> {
  const vars: Record<string, string> = {};
  if (colors.meal) vars[IOB_RING_FIRST_TOKEN] = colors.meal;
  if (colors.correction) vars[IOB_RING_SECOND_TOKEN] = colors.correction;
  if (colors.basal) vars[IOB_RING_THIRD_TOKEN] = colors.basal;
  return vars;
}

const CORRECTION_KEYWORDS = ["correction", "correct", "fix"];

export interface BolusForRings {
  /** Cycle 118: the source treatment id. Lets callers (NowScreen) map a
   *  ring back to its exact treatment row to compute its meal/correction
   *  color — robust to two doses sharing a timestamp, which a `takenAt`
   *  match would collide (both rings would take the first row's color). */
  id: string;
  units: number;
  takenAt: number;
  label: string;
  color: string;
}

/** Cycle 75: one grouped block for the IOB card, regrouped around the
 *  *meal* rather than the bolus. Meal boluses that pair to the same
 *  carbs row collapse into one `meal` block that carries the food
 *  description, the glyph key, and the carb estimate ONCE, plus its
 *  contributing doses (newest-first, ≥1 — empty only for the carbs-only
 *  fallback). A correction bolus is never grouped — it stays its own
 *  standalone `correction` block. Blocks are ordered by their newest
 *  dose, newest-first, so line order still tracks the ring order. */
export type MealContextBlock =
  | {
      kind: "meal";
      /** Food name from the paired carbs row (or the bolus's own
       *  notes); "" when the meal was logged without a description. */
      description: string;
      foodIconKey: FoodIconKey;
      /** Carb estimate in grams, or null when none is known. */
      carbsG: number | null;
      /** Contributing doses, newest-first. Empty only for the
       *  carbs-only fallback (a meal logged with no active bolus). */
      doses: { units: number; takenAt: number }[];
    }
  | { kind: "correction"; units: number; takenAt: number }
  /** Cycle 104c: a meal-type dose (never a correction) with NO meal
   *  context — no paired carbs row and no food notes. In practice only
   *  the chronologically first active dose (forced meal-type by the
   *  precedence-zero rule) with nothing to pair to. Rendered like the
   *  correction block but blue + labeled "Bolus". */
  | { kind: "bolus"; units: number; takenAt: number };

/** Filter to insulin treatments still within the IOB window, newest first.
 *  Returns at most two entries — the BolusRings component renders an outer
 *  ring (oldest still-active bolus) and an inner ring (newer stacked one). */
export function activeBolusesForRings(
  treatments: Treatment[],
  now: number = Date.now(),
): BolusForRings[] {
  const cutoff = now - IOB_DURATION_MS;
  const insulin = treatments
    .filter(
      (t) =>
        t.event_type === "insulin" &&
        // Cycle 121: a long-acting (basal) dose must NEVER enter the bolus
        // path — it would be misread as a ~4h bolus and inflate the rings.
        t.insulin_kind !== "basal" &&
        t.insulin_u != null &&
        new Date(t.timestamp).getTime() >= cutoff,
    )
    .map((t) => ({
      id: t.id,
      units: Number(t.insulin_u),
      takenAt: new Date(t.timestamp).getTime(),
      label: (t.notes ?? "Bolus").slice(0, 24),
      // Cycle 124: default to the first-ring token color (not the pre-104d
      // `var(--accent)` blue). NowScreen / the Settings preview still
      // override this per-dose via `isCorrectionBolus`, but routing the
      // default through the token pair means no consumer can ever emit the
      // old blue — the var(--accent) footgun is gone.
      color: MEAL_BOLUS_COLOR,
    }));

  // Pick the two most recent, then render oldest-first (outer = oldest).
  // Cycle 118b: break exact-timestamp ties by ORIGINAL (treatments) order
  // so the dose `oldestActiveInsulinRow` calls "first" — the earliest in
  // the array, which the cycle-104c rule forces to meal — is the OUTER
  // ring. The previous `sort desc → reverse` flipped tie order against
  // that rule, so a meal + correction logged at the SAME instant put the
  // correction on the outer ring and the meal on the inner: the outer
  // ring rendered the correction color (the operator-reported "swapped
  // rings"). With distinct timestamps the order is unchanged.
  const order = new Map(insulin.map((b, i) => [b, i] as const));
  const newest = [...insulin]
    .sort((a, b) => b.takenAt - a.takenAt) // newest first
    .slice(0, 2); // dual-ring fits two — drop older overflow
  newest.sort((a, b) => a.takenAt - b.takenAt || order.get(a)! - order.get(b)!);
  return newest;
}

/** Linear remaining fraction in [0, 1]. */
export function remainingFraction(takenAt: number, now: number = Date.now()): number {
  const elapsed = now - takenAt;
  if (elapsed <= 0) return 1;
  if (elapsed >= IOB_DURATION_MS) return 0;
  return 1 - elapsed / IOB_DURATION_MS;
}

export function totalIOB(boluses: BolusForRings[], now: number = Date.now()): number {
  return boluses.reduce(
    (sum, b) => sum + b.units * remainingFraction(b.takenAt, now),
    0,
  );
}

/** Cycle 104: milliseconds until insulin-on-board fully clears — the
 *  LONGEST remaining linear-decay window across the active boluses (all
 *  share `IOB_DURATION_MS`, so the newest bolus clears last). Returns 0
 *  when nothing is active. Takes the minimal `{ takenAt }[]` shape so
 *  BOTH callers feed the same function from the data they already have —
 *  the `BolusRings` countdown line (its `BolusForRings[]`) and the
 *  cycle-101 spoken "time until IOB clears" intent (its active-bolus
 *  list) — so the displayed countdown and the spoken answer can never
 *  disagree. The ≤2-ring cap doesn't change the result: the single
 *  newest bolus (which determines the max) is always within the two
 *  newest. */
export function iobTimeRemainingMs(
  boluses: { takenAt: number }[],
  now: number = Date.now(),
): number {
  let max = 0;
  for (const b of boluses) {
    const remaining = IOB_DURATION_MS - (now - b.takenAt);
    if (remaining > max) max = remaining;
  }
  return max;
}

// ─── Cycle 74b: meal / correction display derivation ─────────────────

function timeMs(t: Treatment): number {
  return new Date(t.timestamp).getTime();
}

/** The (≤2) newest active insulin rows within the IOB window,
 *  newest-first — the same set the two rings show, so the grouped
 *  blocks never carry more than two contributing doses total. */
function activeInsulinRowsNewestFirst(
  treatments: Treatment[],
  now: number,
): Treatment[] {
  const cutoff = now - IOB_DURATION_MS;
  const rows = treatments.filter(
    (t) =>
      t.event_type === "insulin" &&
      t.insulin_kind !== "basal" && // cycle 121: basal never pairs/rings
      t.insulin_u != null &&
      timeMs(t) >= cutoff,
  );
  rows.sort((a, b) => timeMs(b) - timeMs(a)); // newest first
  return rows.slice(0, 2);
}

/** The carbs row paired to a bolus (nearest within the meal window and
 *  the max-age horizon), or null. */
function pairedCarbsRow(
  bolus: Treatment,
  treatments: Treatment[],
  now: number,
): Treatment | null {
  const cutoff = now - MEAL_CONTEXT_MAX_AGE_MS;
  const bt = timeMs(bolus);
  const candidates = treatments.filter(
    (t) =>
      t.event_type === "carbs" &&
      t.carbs_g != null &&
      Number(t.carbs_g) > 0 &&
      timeMs(t) >= cutoff &&
      Math.abs(timeMs(t) - bt) <= MEAL_PAIR_WINDOW_MS,
  );
  candidates.sort(
    (a, b) => Math.abs(timeMs(a) - bt) - Math.abs(timeMs(b) - bt),
  );
  return candidates[0] ?? null;
}

/** The chronologically OLDEST active insulin row in the IOB window (the
 *  "first" dose still on board), or null when none is active. Used by the
 *  cycle-104c precedence-zero rule below. */
function oldestActiveInsulinRow(
  treatments: Treatment[],
  now: number,
): Treatment | null {
  const cutoff = now - IOB_DURATION_MS;
  let oldest: Treatment | null = null;
  for (const t of treatments) {
    if (
      t.event_type === "insulin" &&
      t.insulin_kind !== "basal" && // cycle 121: basal isn't a bolus
      t.insulin_u != null &&
      timeMs(t) >= cutoff
    ) {
      if (oldest == null || timeMs(t) < timeMs(oldest)) oldest = t;
    }
  }
  return oldest;
}

/** Decide whether an active insulin row is a correction (vs. a meal
 *  bolus). Precedence: (cycle 126) an explicit `dose_intent` decides →
 *  (0) the first active dose is never a correction → explicit correction
 *  keyword → explicit meal signal (food note / own carbs) → (cycle 125) a
 *  stacked dose with insulin already on board defaults to a correction.
 *  Pure — no hooks, no fetch. Single source of truth for the ring color,
 *  the line dot + label, and the modal. */
export function isCorrectionBolus(
  bolus: Treatment,
  treatments: Treatment[],
  now: number = Date.now(),
): boolean {
  // Cycle 126: an explicit, user-chosen intent from the guided Insulin
  // flow is authoritative — it wins over every inference rule below
  // (including the cycle-104c "oldest active is never a correction", so a
  // first/only dose the user logged as a Correction reads as one). Only a
  // row that carries no intent (null/undefined — every pre-126 row and
  // every non-guided write: Siri, kiosk voice, legacy) falls through to
  // the unchanged Cycle 125 inference.
  if (bolus.dose_intent === "correction") return true;
  if (bolus.dose_intent === "meal") return false;

  // 0. Cycle 104c: the chronologically oldest active insulin row is NEVER
  // a correction — a correction by definition corrects a PRIOR dose/meal,
  // so only a subsequent dose can be one. This rule is absolute: it wins
  // over an explicit correction keyword on that first dose. A "bolus or
  // basal" first dose therefore always reads as the meal-type first bolus.
  const oldest = oldestActiveInsulinRow(treatments, now);
  if (oldest != null && oldest.id === bolus.id) return false;

  // Past rule 0 this is a STACKED dose — there is already insulin on board
  // from an earlier active dose. Explicit per-dose meal/correction signals
  // still win, in order:
  const notes = (bolus.notes ?? "").toLowerCase();
  // 1. An explicit "correction"/"correct"/"fix" note → correction.
  if (notes && CORRECTION_KEYWORDS.some((k) => notes.includes(k))) return true;
  // 2. An explicit food note → meal.
  if (notes && matchesFoodKeyword(notes)) return false;
  // 3. A combined row carrying its OWN carbs is unambiguously a meal dose.
  if (bolus.carbs_g != null && Number(bolus.carbs_g) > 0) return false;

  // 4. Cycle 125 (operator directive): with insulin already on board, the
  // NEXT injection defaults to a CORRECTION. The first dose already covered
  // the meal, so a stacked dose while it's still working is a correction
  // unless it carries its own explicit meal signal (handled above). We
  // deliberately do NOT infer "meal" from mere proximity to the first
  // dose's carbs row anymore — that nearby carbs row belongs to the first
  // dose, and inferring a second meal from it is exactly what forced the
  // operator to hand-type "correction" on every stacked dose to recolor it.
  return true;
}

/** Cycle 453.4 (operator decision): what the meal-context line and the
 *  ActiveBoluses header say for a meal bolus whose OWN `carbs_g` is an
 *  explicit 0 and that pairs to no carbs row. Never "0g" / "~0g". */
export const NO_CARBS_LABEL = "No carbs";

/** A bolus row that carries its own explicit 0 g — entered, not missing. */
function hasExplicitZeroCarbs(bolus: Treatment): boolean {
  return bolus.carbs_g != null && Number(bolus.carbs_g) === 0;
}

/** Resolve the food description + carbs for a meal bolus: a combined
 *  row's own carbs, else the paired carbs row, else the bolus's own
 *  notes with no carbs figure — or, cycle 453.4, `carbsG: 0` when the
 *  bolus itself says 0 g and nothing pairs (the renderers print
 *  `NO_CARBS_LABEL` for exactly 0). Pairing is unchanged: an explicit 0
 *  still pairs to a nearby carbs row, which then wins as it always has. */
function mealPairing(
  bolus: Treatment,
  treatments: Treatment[],
  now: number,
): { food?: string; carbsG?: number } {
  // Cycle 104c: a correction-keyword note ("correction"/"correct"/"fix")
  // is not a meal name — don't surface it as the food description (so a
  // forced-meal first dose carrying such a note reads as a bare "Bolus",
  // not a meal called "correction"). A real food note is preserved.
  const rawNotes = (bolus.notes ?? "").trim();
  const nl = rawNotes.toLowerCase();
  const ownNotes =
    rawNotes && !CORRECTION_KEYWORDS.some((k) => nl.includes(k))
      ? rawNotes
      : undefined;
  if (bolus.carbs_g != null && Number(bolus.carbs_g) > 0) {
    return { food: ownNotes, carbsG: Number(bolus.carbs_g) };
  }
  const carbs = pairedCarbsRow(bolus, treatments, now);
  if (carbs) {
    return {
      food: (carbs.notes ?? "").trim() || ownNotes,
      carbsG: Number(carbs.carbs_g),
    };
  }
  if (hasExplicitZeroCarbs(bolus)) return { food: ownNotes, carbsG: 0 };
  return { food: ownNotes, carbsG: undefined };
}

/** The display context for a single bolus row in the
 *  `ActiveBolusesModal` editor: whether it reads as a correction, and
 *  (for a meal bolus) the paired food name + carb estimate + glyph key.
 *  The food lives on a separate carbs row, so without this the modal
 *  would show an empty notes field with no hint of which meal the bolus
 *  covered. Pure — wraps `isCorrectionBolus` + `mealPairing`. */
export interface BolusMealContext {
  isCorrection: boolean;
  food: string | null;
  carbsG: number | null;
  foodIconKey: FoodIconKey;
}

export function bolusMealContext(
  bolus: Treatment,
  treatments: Treatment[],
  now: number = Date.now(),
): BolusMealContext {
  if (isCorrectionBolus(bolus, treatments, now)) {
    return { isCorrection: true, food: null, carbsG: null, foodIconKey: "meal" };
  }
  const { food, carbsG } = mealPairing(bolus, treatments, now);
  const description = (food ?? "").trim();
  return {
    isCorrection: false,
    food: description || null,
    carbsG: carbsG ?? null,
    foodIconKey: foodIconKey(description || null),
  };
}

/** One group of bolus rows that share a meal, for the
 *  `ActiveBolusesModal` editor. Cycle 75a hotfix: the modal renders the
 *  meal context ONCE as a read-only header above its rows instead of
 *  repeating it per row. */
export interface BolusMealGroup {
  /** Stable key — the paired carbs row's id for a meal, else the bolus's
   *  own id (combined-row meal, food-note-only meal, or correction). */
  key: string;
  /** Meal context shared by every bolus in this group (all members pair
   *  to the same carbs row, so it's identical across them). */
  context: BolusMealContext;
  /** The group's bolus rows, in the order they were given (the caller
   *  passes newest-first, so groups come out ordered by newest bolus). */
  boluses: Treatment[];
}

/** Group an arbitrary list of bolus rows by meal, using the SAME
 *  paired-carbs-row key the Cycle 75 card (`mealForContext`) uses — so
 *  the modal and the card can never disagree on what pairs with what.
 *  Unlike `mealForContext` (which caps at the 2-ring budget), this groups
 *  every bolus the caller passes, so a 3rd+ stacked bolus is grouped too.
 *  Each correction stands alone. Pure. */
export function groupBolusesByMeal(
  boluses: Treatment[],
  treatments: Treatment[],
  now: number = Date.now(),
): BolusMealGroup[] {
  const groups: BolusMealGroup[] = [];
  const byKey = new Map<string, BolusMealGroup>();
  for (const bolus of boluses) {
    const context = bolusMealContext(bolus, treatments, now);
    let key: string;
    if (context.isCorrection) {
      // Each correction is its own group — never merged under a meal.
      key = `correction:${bolus.id}`;
    } else {
      const ownCarbs = bolus.carbs_g != null && Number(bolus.carbs_g) > 0;
      const carbsRow = ownCarbs ? null : pairedCarbsRow(bolus, treatments, now);
      key = carbsRow ? `carbs:${carbsRow.id}` : `bolus:${bolus.id}`;
    }
    const existing = byKey.get(key);
    if (existing) {
      existing.boluses.push(bolus);
    } else {
      const g: BolusMealGroup = { key, context, boluses: [bolus] };
      byKey.set(key, g);
      groups.push(g);
    }
  }
  return groups;
}

/** Resolve the grouped meal/correction blocks from existing treatment
 *  rows. Cycle 75 regroups around the meal: the (≤2) newest active
 *  boluses are classified, meal boluses that pair to the SAME carbs row
 *  collapse into one block (food + carb estimate carried once, doses
 *  listed newest-first), and corrections stand alone. When no bolus is
 *  active but a recent carbs row exists, returns a single meal block
 *  with no doses. Blocks are ordered by their newest dose, newest-first
 *  (matching the ring order). Empty array → the card still mounts with
 *  its empty state. Pure — no hooks, no fetch. */
export function mealForContext(
  treatments: Treatment[],
  now: number = Date.now(),
): MealContextBlock[] {
  const rows = activeInsulinRowsNewestFirst(treatments, now); // newest-first, ≤2

  if (rows.length === 0) {
    // No active bolus — the most recent carbs row alone still resolves
    // to a single meal block (no doses).
    const cutoff = now - MEAL_CONTEXT_MAX_AGE_MS;
    const recentCarbs = treatments
      .filter(
        (t) =>
          t.event_type === "carbs" &&
          t.carbs_g != null &&
          Number(t.carbs_g) > 0 &&
          timeMs(t) >= cutoff,
      )
      .sort((a, b) => timeMs(b) - timeMs(a))[0];
    if (recentCarbs) {
      const description = (recentCarbs.notes ?? "").trim();
      return [
        {
          kind: "meal",
          description,
          foodIconKey: foodIconKey(description || null),
          carbsG: Number(recentCarbs.carbs_g),
          doses: [],
        },
      ];
    }
    return [];
  }

  // Group meal boluses by the identity of their paired carbs row;
  // corrections stand alone. `orderAt` (the block's newest dose) drives
  // the final newest-first ordering so line order tracks the rings.
  type MealAcc = Extract<MealContextBlock, { kind: "meal" }>;
  interface Slot {
    block: MealContextBlock;
    orderAt: number;
    meal?: MealAcc;
  }
  const slots: Slot[] = [];
  const mealGroups = new Map<string, Slot>();
  // Cycle 119: the identities of the carbs rows that pair to a rendered
  // meal block. Used after the loop to suppress double-rendering a carbs
  // row both inside its grouped meal block AND as a standalone food block.
  const pairedCarbsIds = new Set<string>();

  for (const bolus of rows) {
    const units = Number(bolus.insulin_u);
    const takenAt = timeMs(bolus);

    if (isCorrectionBolus(bolus, treatments, now)) {
      slots.push({ block: { kind: "correction", units, takenAt }, orderAt: takenAt });
      continue;
    }

    // Meal bolus. A combined row (carrying its own carbs) keys to itself
    // — its meal is intrinsic, not a shared carbs row. Otherwise the key
    // is the paired carbs row's identity, so two boluses that match the
    // same carbs row collapse into one block.
    const ownCarbs = bolus.carbs_g != null && Number(bolus.carbs_g) > 0;
    const carbsRow = ownCarbs ? null : pairedCarbsRow(bolus, treatments, now);
    if (carbsRow) pairedCarbsIds.add(carbsRow.id);
    const key = carbsRow ? `carbs:${carbsRow.id}` : `bolus:${bolus.id}`;

    const existing = mealGroups.get(key);
    if (existing?.meal) {
      // rows are newest-first, so appending keeps doses newest-first.
      existing.meal.doses.push({ units, takenAt });
      existing.orderAt = Math.max(existing.orderAt, takenAt);
      continue;
    }

    const { food, carbsG } = mealPairing(bolus, treatments, now);
    const description = (food ?? "").trim();
    // Cycle 104c: a meal-type dose with NO context (no food name, no
    // carbs) — in practice the forced-meal first dose with nothing to
    // pair to — renders as a compact blue "Bolus" block, not a blank
    // "Meal" block. (Each bare dose keys to its own `bolus:<id>`, so this
    // never groups a second dose.)
    if (description === "" && carbsG == null) {
      slots.push({ block: { kind: "bolus", units, takenAt }, orderAt: takenAt });
      continue;
    }
    const meal: MealAcc = {
      kind: "meal",
      description,
      foodIconKey: foodIconKey(description || null),
      carbsG: carbsG ?? null,
      doses: [{ units, takenAt }],
    };
    const slot: Slot = { block: meal, orderAt: takenAt, meal };
    mealGroups.set(key, slot);
    slots.push(slot);
  }

  // Cycle 119: a logged meal must stay visible on the card for its full
  // context window even when no bolus pairs to it — either because the
  // active bolus aged past `MEAL_PAIR_WINDOW_MS` (logged a meal, then
  // bolused 45 min later) or because insulin on board belongs to an
  // entirely separate dose. The per-bolus loop above only emits blocks
  // for boluses, so before this fix an unpaired carbs row was silently
  // dropped whenever any bolus was active (the food block "vanished"
  // when a bolus was logged). Now any carbs row within the max-age
  // horizon that is NOT the paired carbs row of a rendered meal block
  // also yields a carbs-only block (the same empty-`doses` shape the
  // no-bolus fallback above produces), so food and active insulin
  // coexist. Cap at 1 — the most recent unpaired carbs row — to protect
  // the kiosk height budget; older unpaired meals are dropped silently.
  const carbsCutoff = now - MEAL_CONTEXT_MAX_AGE_MS;
  const unpairedCarbs = treatments
    .filter(
      (t) =>
        t.event_type === "carbs" &&
        t.carbs_g != null &&
        Number(t.carbs_g) > 0 &&
        timeMs(t) >= carbsCutoff &&
        !pairedCarbsIds.has(t.id),
    )
    .sort((a, b) => timeMs(b) - timeMs(a))[0];
  if (unpairedCarbs) {
    const description = (unpairedCarbs.notes ?? "").trim();
    slots.push({
      block: {
        kind: "meal",
        description,
        foodIconKey: foodIconKey(description || null),
        carbsG: Number(unpairedCarbs.carbs_g),
        doses: [],
      },
      orderAt: timeMs(unpairedCarbs),
    });
  }

  slots.sort((a, b) => b.orderAt - a.orderAt);
  return slots.map((s) => s.block);
}

// ─── Cycle 121: long-acting (basal) fuel line ────────────────────────

function clamp01(x: number): number {
  if (x < 0) return 0;
  if (x > 1) return 1;
  return x;
}

/** Cycle 123: the per-dose minutes of action for a basal row — the row's
 *  `insulin_duration_min`, or `DEFAULT_BASAL_DURATION_MIN` (1440) when
 *  null/non-positive. The single place the basal duration is resolved, so
 *  `insulinRowRemainingMs` and `basalForContext` agree. */
function basalDurationMin(row: Treatment): number {
  return row.insulin_duration_min != null && row.insulin_duration_min > 0
    ? row.insulin_duration_min
    : DEFAULT_BASAL_DURATION_MIN;
}

/** Cycle 123: remaining milliseconds of action for a SINGLE insulin
 *  treatment, honoring `insulin_kind`. A `basal` dose uses its own
 *  long-acting duration; a `bolus` — and a missing/cached-undefined kind,
 *  which reads as `'bolus'` per the cycle-121 safe default — uses the ~4h
 *  `IOB_DURATION_MS` decay. Floored at 0. This is the ONE source both the
 *  `ActiveBolusesModal` per-row time-left badge AND `basalForContext`'s
 *  `remainingFrac` / `hoursLeft` derive from, so the editor and the
 *  front-card `BasalLine` can never show a different basal time-left for
 *  the same dose. (The whole-card `iobTimeRemainingMs` bolus countdown is
 *  untouched.) */
export function insulinRowRemainingMs(
  row: Treatment,
  now: number = Date.now(),
): number {
  if (row.insulin_kind === "basal") {
    const durationMs = basalDurationMin(row) * 60_000;
    return clamp01(1 - (now - timeMs(row)) / durationMs) * durationMs;
  }
  return Math.max(0, IOB_DURATION_MS - (now - timeMs(row)));
}

/** Cycle 123: the basal "hours left" badge string — the single phrasing
 *  both the front-card `BasalLine` and the modal's basal row use, so they
 *  match byte-for-byte: "0h" when due, "≈Nh left" at ≥1h, "<1h left"
 *  otherwise. */
export function formatBasalHoursLeft(hoursLeft: number, due: boolean): string {
  if (due) return "0h";
  if (hoursLeft >= 1) return `≈${Math.round(hoursLeft)}h left`;
  return "<1h left";
}

/** The display context for the basal "L" line on the IOB card. The field
 *  names are a contract the <BasalLine> component reads. */
export interface BasalContext {
  /** Product name ("Lantus" / "Levemir" / …) or null → a neutral "Basal". */
  label: string | null;
  units: number;
  takenAt: number;
  /** Per-dose minutes of action (off the row; 1440 when the row's
   *  duration is null). */
  durationMin: number;
  /** Fraction of the dose's action window still remaining, in [0, 1]. */
  remainingFrac: number;
  /** Hours of action left (remainingFrac × durationMin / 60). */
  hoursLeft: number;
  /** true once the dose has fully lapsed (remainingFrac <= 0) → "take your
   *  Lantus" nudge. */
  due: boolean;
}

/** The most recent active basal dose for the card's "L" line, or null when
 *  the member isn't on tracked basal (no basal row in the 48h lookback → the
 *  line does not render). A basal row that exists but has lapsed returns
 *  `due:true, remainingFrac:0` so the nudge stays on screen. Pure — reads
 *  the SAME merged treatments map the rings consume, so editing/deleting a
 *  basal row updates the line via the existing `treatment` /
 *  `treatment_deleted` WS frames with no new frame type. */
export function basalForContext(
  treatments: Treatment[],
  now: number = Date.now(),
): BasalContext | null {
  const cutoff = now - BASAL_LOOKBACK_MS;
  let latest: Treatment | null = null;
  for (const t of treatments) {
    if (
      t.event_type === "insulin" &&
      t.insulin_kind === "basal" &&
      t.insulin_u != null &&
      timeMs(t) >= cutoff
    ) {
      if (latest == null || timeMs(t) > timeMs(latest)) latest = t;
    }
  }
  if (latest == null) return null;

  // Cycle 123: derive remaining time from the shared insulinRowRemainingMs
  // so this front-card figure and the ActiveBolusesModal basal row are the
  // same computation. (External return shape unchanged.)
  const durationMin = basalDurationMin(latest);
  const takenAt = timeMs(latest);
  const remainingMs = insulinRowRemainingMs(latest, now);
  const remainingFrac = clamp01(remainingMs / (durationMin * 60_000));
  const hoursLeft = remainingMs / 3_600_000;
  return {
    label: latest.insulin_label ?? null,
    units: Number(latest.insulin_u),
    takenAt,
    durationMin,
    remainingFrac,
    hoursLeft,
    due: remainingMs <= 0,
  };
}
